import axios from 'axios';

import { BASE_URL } from '../insights/api/insights';

/** @typedef {import('./advanced-shipping-notices/constants').AdvancedShippingNotice} AdvancedShippingNotice */
/** @typedef {import('./advanced-shipping-notices/constants').AsnFreightConfiguration} AsnFreightConfiguration */
/** @typedef {import('./advanced-shipping-notices/components/advanced-shipping-notice-drawer/steps/select-products/products-import-v2/asn-import-context').ImportedAsnProduct} ImportedAsnProduct */

/**
 * @param {object} params
 * @param {string} params.startDate
 * @param {string} params.endDate
 * @returns {Promise<object>}
 */
export const getFulfillmentOverview = async (params) => {
  return (await axios.get(`${BASE_URL}/fulfillmentOverview`, { params })).data;
};

/**
 * @param {object} params
 * @param {string} params.startDate
 * @param {string} params.endDate
 * @returns {Promise<object>}
 */
export const getFulfillmentOverviewRaw = async (params) => {
  return (await axios.get(`${BASE_URL}/fulfillmentOverviewRaw`, { params })).data;
};

/**
 * @param {number} sellerFillProviderId
 * @param {AdvancedShippingNotice} advancedShippingNotice
 * @returns {Promise<AdvancedShippingNotice>}
 */
export const createNewAsn = async (sellerFillProviderId, advancedShippingNotice) => {
  const response = await axios.post('/asns/create', {
    sellerFillProviderId,
    advancedShippingNotice,
  });
  return response.data;
};

/**
 * @param {number} sellerFillProviderId
 * @param {AdvancedShippingNotice} advancedShippingNotice
 * @returns {Promise<AdvancedShippingNotice>}
 */
export const updateAsn = async (sellerFillProviderId, advancedShippingNotice) => {
  const response = await axios.put('/asns/update', {
    sellerFillProviderId,
    advancedShippingNotice,
  });
  return response.data;
};

/**
 * @param {number} sellerFillProviderId
 * @param {object} [pagingOptions]
 * @param {number} [pagingOptions.page]
 * @param {number} [pagingOptions.pageSize]
 * @param {string} [pagingOptions.statusList]
 * @returns {Promise<{page: number, pageSize: number, totalRecords: number, items: AdvancedShippingNotice[]}>}
 */
export const getPaginatedAsns = async (sellerFillProviderId, pagingOptions = {}) => {
  const { page = 1, pageSize = 10, statusList = null } = pagingOptions;
  const response = await axios.get('/asns', {
    params: {
      sellerFillProviderId,
      page,
      pageSize,
      statusList,
    },
  });
  return response.data;
};

/**
 * @param {number} sellerFillProviderId
 * @returns {Promise<object[]>}
 */
export const getFulfillmentWarehouses = async (sellerFillProviderId) => {
  const response = await axios.get('/asns/listWarehouses', {
    params: { sellerFillProviderId },
  });
  return response.data;
};

/**
 * @param {number} sellerFillProviderId
 * @param {string} asnGuid
 * @returns {Promise<AdvancedShippingNotice>}
 */
export const getAsnWithId = async (sellerFillProviderId, asnGuid) => {
  const response = await axios.get(`/asns/${asnGuid}`, {
    params: { sellerFillProviderId },
  });
  return response.data;
};

/**
 * This is the submit endpoint that triggers the product sync and waits for it to complete before returning.
 * @param {number} sellerFillProviderId
 * @param {string} asnGuid
 * @param {AdvancedShippingNotice} advancedShippingNotice
 * @returns {Promise<AdvancedShippingNotice>}
 */
export const submitAsn = async (sellerFillProviderId, asnGuid, advancedShippingNotice) => {
  const response = await axios.post('/asns/submit', {
    sellerFillProviderId,
    asnGuid,
    advancedShippingNotice,
  });
  return response.data;
};

/**
 * This is the same as submitAsn but backend doesn't sync products to fulfillment provider before submission
 * It is assumed that the products have already been synced via the syncProducts action/epic
 * @param {number} sellerFillProviderId
 * @param {string} asnGuid
 * @param {AdvancedShippingNotice} advancedShippingNotice
 * @returns {Promise<string>}
 */
export const submitAsnOnly = async (sellerFillProviderId, asnGuid, advancedShippingNotice) => {
  const response = await axios.post('/asns/submitOnly', {
    sellerFillProviderId,
    asnGuid,
    advancedShippingNotice,
  });
  return response.data;
};

/**
 * @param {number} sellerFillProviderId
 * @param {string} asnGuid
 * @returns {Promise<void>}
 */
export const cancelAsn = async (sellerFillProviderId, asnGuid) => {
  const response = await axios.post('/asns/cancel', {
    sellerFillProviderId,
    asnGuid,
  });
  return response.data;
};

/**
 * @param {number} sellerFillProviderId
 * @param {string} asnGuid
 * @returns {Promise<void>}
 */
export const deleteAsn = async (sellerFillProviderId, asnGuid) => {
  const response = await axios.delete(`/asns/${asnGuid}`, {
    params: { sellerFillProviderId },
  });
  return response.data;
};

/**
 * @param {number} sellerFillProviderId
 * @param {string} asnGuid
 * @param {AsnFreightConfiguration} freightConfiguration
 * @returns {Promise<AdvancedShippingNotice>}
 */
export const submitFreightConfiguration = async (sellerFillProviderId, asnGuid, freightConfiguration) => {
  const response = await axios.post(`/asns/${asnGuid}/freightConfiguration`, freightConfiguration, {
    params: { sellerFillProviderId },
  });
  return response.data;
};

/**
 * @param {FormData} formData
 * @returns {Promise<{success: boolean, validatedProducts: ImportedAsnProduct[], errors: string[], processedCount: number}>}
 */
export const importProductsToAsn = async (formData) => {
  const response = await axios.post('/asns/importProducts', formData, {
    headers: {
      ContentType: 'multipart/form-data',
    },
  });

  return response.data;
};
